import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  connectOwner,
  joinLiveConversation,
  leaveLiveConversation,
  focusConversation,
  sendOwnerMessage,
} from "../../utils/ownerSocket";

// One socket for the whole inbox. An owner can hold several conversations at
// once, so join state is a set rather than a single id, and switching rows only
// changes which one is focused — the rest stay live in the background.

const now = () =>
  new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

// The socket speaks in raw roles; the thread renders the same three the REST
// mapper produces, plus "system" for the notes the inbox writes itself.
const entryFor = (role, text, names) => {
  if (role === "system") {
    return { role: "system", sender: "", time: now(), text };
  }

  const isOwner = role === "owner";
  const isAssistant = role === "assistant";

  return {
    role: isOwner ? "agent" : isAssistant ? "assistant" : "visitor",
    sender: isOwner ? "You" : isAssistant ? names.assistant : names.visitor,
    time: now(),
    text,
  };
};

/**
 * @param {string} assistantId
 * @param {string} focusedId the conversation on screen
 * @param {{assistant: string, visitor: string}} names for message labels
 */
export default function useOwnerInbox(assistantId, focusedId, names) {
  const [connected, setConnected] = useState(false);
  const [joined, setJoined] = useState(() => new Set());
  const [unread, setUnread] = useState(() => new Set());
  // Conversations any owner session holds, so the list can mark them live even
  // when it was another tab that joined.
  const [heldByAnyone, setHeldByAnyone] = useState(() => new Set());
  const [threads, setThreads] = useState({});
  const [joining, setJoining] = useState("");
  const [error, setError] = useState("");

  const socketRef = useRef(null);
  // Read inside socket handlers, which capture their first render otherwise.
  const focusedRef = useRef(focusedId);
  const namesRef = useRef(names);
  const joinedRef = useRef(new Set());

  focusedRef.current = focusedId;
  namesRef.current = names;

  const addEntry = useCallback((conversationId, role, text) => {
    if (!conversationId) return;

    setThreads((prev) => ({
      ...prev,
      [conversationId]: [
        ...(prev[conversationId] || []),
        entryFor(role, text, namesRef.current),
      ],
    }));

    // A message on a conversation that isn't on screen is what the row badge
    // is for; the server raises its own notification for the same case.
    if (conversationId !== focusedRef.current && role !== "system") {
      setUnread((prev) => new Set(prev).add(conversationId));
    }
  }, []);

  const drop = useCallback((conversationId) => {
    setJoined((prev) => {
      const next = new Set(prev);
      next.delete(conversationId);
      joinedRef.current = next;
      return next;
    });
  }, []);

  useEffect(() => {
    if (!assistantId) return undefined;

    setConnected(false);
    setJoined(new Set());
    setUnread(new Set());
    setHeldByAnyone(new Set());
    setThreads({});
    setError("");
    joinedRef.current = new Set();

    const socket = connectOwner(assistantId, {
      onConnect: () => {
        setConnected(true);
        // The server needs to know what is on screen from the outset.
        focusConversation(socket, focusedRef.current);
      },

      onDisconnect: () => {
        setConnected(false);
        setJoined(new Set());
        joinedRef.current = new Set();
      },

      onJoined: (data) => {
        const id = data.conversationId;
        if (!id) return;

        setJoining("");
        setJoined((prev) => {
          const next = new Set(prev).add(id);
          joinedRef.current = next;
          return next;
        });
        setHeldByAnyone((prev) => new Set(prev).add(id));

        // The server's history replaces whatever was there: it is the same
        // transcript the REST endpoint served, so appending would double it.
        setThreads((prev) => ({
          ...prev,
          [id]: (data.messages || []).map((message) =>
            entryFor(message.role, message.content, namesRef.current),
          ),
        }));

        addEntry(
          id,
          "system",
          "You joined — the assistant won't reply here until you leave.",
        );
      },

      onSessionReplaced: (data) => {
        drop(data.conversationId);
        addEntry(
          data.conversationId,
          "system",
          "Another session took over this conversation, so you were signed out of it.",
        );
      },

      onAutoResumed: (data) => {
        drop(data.conversationId);
        addEntry(
          data.conversationId,
          "system",
          "You were idle for 5 minutes, so the assistant has taken this conversation back.",
        );
      },

      onOwnerJoined: (data) => {
        const id = data.conversationId;
        setHeldByAnyone((prev) => new Set(prev).add(id));

        // Ours already announced itself through the join ack.
        if (!joinedRef.current.has(id)) {
          addEntry(id, "system", "Someone from your team joined this conversation.");
        }
      },

      onOwnerLeft: (data) => {
        const id = data.conversationId;

        setHeldByAnyone((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });

        if (joinedRef.current.has(id)) {
          addEntry(id, "system", "Left — the assistant has resumed answering.");
          drop(id);
        }
      },

      onVisitorMessage: (data) => addEntry(data.conversationId, "user", data.content),
      onOwnerMessage: (data) => addEntry(data.conversationId, "owner", data.content),

      onError: (message) => {
        setError(message);
        setJoining("");
      },
    });

    socketRef.current = socket;

    return () => {
      // Every held conversation is handed back before the socket goes, or the
      // server would keep them paused with nobody answering.
      joinedRef.current.forEach((id) => leaveLiveConversation(socket, id));
      socket.disconnect();
      socketRef.current = null;
      joinedRef.current = new Set();
    };
  }, [assistantId, addEntry, drop]);

  // Focus follows the selection. Without it a message on a conversation that
  // IS on screen still raises a notification.
  useEffect(() => {
    focusConversation(socketRef.current, focusedId);

    if (!focusedId) return;

    setUnread((prev) => {
      if (!prev.has(focusedId)) return prev;

      const next = new Set(prev);
      next.delete(focusedId);
      return next;
    });
  }, [focusedId, connected]);

  // Leaving the tab means nothing is on screen, so notifications should fire
  // again for whatever is joined.
  useEffect(() => {
    const onVisibility = () => {
      focusConversation(
        socketRef.current,
        document.hidden ? "" : focusedRef.current,
      );
    };

    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  const join = useCallback((conversationId) => {
    if (!conversationId) return;
    setError("");
    setJoining(conversationId);
    joinLiveConversation(socketRef.current, conversationId);
  }, []);

  const leave = useCallback((conversationId) => {
    leaveLiveConversation(socketRef.current, conversationId);
  }, []);

  const send = useCallback((conversationId, text) => {
    sendOwnerMessage(socketRef.current, conversationId, text);
  }, []);

  return useMemo(
    () => ({
      connected,
      joined,
      unread,
      heldByAnyone,
      threads,
      joining,
      error,
      join,
      leave,
      send,
    }),
    [connected, joined, unread, heldByAnyone, threads, joining, error, join, leave, send],
  );
}
